import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import api, { clearShopSession, SESSION_KEY, TOKEN_KEY } from '../services/api';
import type { Role, Session } from '../types';
import { getDeviceId } from '../utils/device';

interface AuthValue {
  session: Session | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<Session>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setToken: (token: string) => void;
  /** Feature switch on for this shop (and its plan). */
  has: (feature: string) => boolean;
  isRole: (...roles: Role[]) => boolean;
  setting: <T = unknown>(key: string, fallback?: T) => T;
}

const Ctx = createContext<AuthValue | null>(null);

function stored(): Session | null {
  try {
    return localStorage.getItem(TOKEN_KEY) ? JSON.parse(localStorage.getItem(SESSION_KEY) || 'null') : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(stored);
  const [loading, setLoading] = useState<boolean>(!!localStorage.getItem(TOKEN_KEY));

  const save = (s: Session | null) => {
    setSession(s);
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else localStorage.removeItem(SESSION_KEY);
  };

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get<Session>('/auth/me');
      save(data);
    } catch {
      /* the interceptor signs out on 401/402 */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (username: string, password: string) => {
    const { data } = await api.post('/auth/login', {
      username: username.trim(), password, device_id: getDeviceId(),
    });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const s: Session = { user: data.user, shop: data.shop, counter: data.counter, must_change_password: data.must_change_password };
    save(s);
    if (!data.must_change_password) await refresh();
    return s;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* already gone */ }
    clearShopSession();
    setSession(null);
  };

  const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);

  const value = useMemo<AuthValue>(() => ({
    session, loading, login, logout, refresh, setToken,
    has: (f) => !!session?.shop.features?.[f],
    isRole: (...roles) => !!session && roles.includes(session.user.role),
    setting: <T,>(key: string, fallback?: T) => ((session?.shop.settings?.[key] ?? fallback) as T),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [session, loading, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
